import type { Chart, ChartOptions } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import commaNumber from 'comma-number';
import { useMemo, useRef } from 'react';
import { Chart as ChartJS } from 'react-chartjs-2';

import {
  CHART_CALCULATION,
  CHART_METRIC_TYPE,
} from '@/entities/chart/model/constants';
import { getChartMetricLabel } from '@/features/(charts)/chart-metric-policy';
import useEventPlugin from '@/features/(charts)/chart-renderer/hooks/use-chart-event-plugin';
import commonChartOptions from '@/features/(charts)/chart-renderer/lib/common-chart-options';
import { ChartMetricTypeMap } from '@/features/(charts)/chart-renderer/model/chart-maps';
import useChartComboData from '@/features/(charts)/chart-renderer/ui/Single/useChartComboData';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import { useChartTooltip } from '@/shared/ui/charts';

import ChartCustomTooltip from '../chart-custom-tooltip/ChartCustomTooltip';

type Props = {
  tableData: ChartTableRes;
  chart: ChartBase & { name: string };
  dashboardCardInfo?: DashboardCardInfoT;
  yAxisTitle?: string;
};

const ComboChart = (props: Props) => {
  const chartRef = useRef<Chart>(null);
  const { comboChartData } = useChartComboData(props);
  const { tooltipData, chartTooltip, hideTooltip } = useChartTooltip({
    chart: chartRef.current,
  });

  const eventPlugin = useEventPlugin({
    meta: props.chart,
    hideTooltip,
    dashboardCardInfo: props.dashboardCardInfo,
  });

  const isPrimaryAvgScoreType =
    ChartMetricTypeMap.get(props.chart.series[0].measure.metric) ===
      CHART_METRIC_TYPE.SCORE &&
    props.chart.series[0].measure.calculation === CHART_CALCULATION.AVERAGE;

  const isSecondaryAvgScoreType =
    props.chart.series[1].measure &&
    ChartMetricTypeMap.get(props.chart.series[1].measure.metric) ===
      CHART_METRIC_TYPE.SCORE &&
    props.chart.series[1].measure.calculation === CHART_CALCULATION.AVERAGE;

  const comboChartOptions: ChartOptions = useMemo(() => {
    return {
      events: ['mouseleave', 'mousemove', 'click'],
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      scales: {
        x: {
          stacked: false,
        },
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          grace: '30%',
          grid: {
            color: 'transparent',
          },
          ticks: isPrimaryAvgScoreType
            ? {
                callback: (value) => Number(value),
                stepSize: 1,
              }
            : {
                callback: (value) => commaNumber(Number(value)),
              },
          title: {
            display: true,
            text: getChartMetricLabel(props.chart.series[0].measure.metric) ||
              'Primary',
          },
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          grace: '15%',
          grid: {
            drawOnChartArea: false,
          },
          ticks: isSecondaryAvgScoreType
            ? {
                callback: (value) => Number(value),
                stepSize: 1,
              }
            : {
                callback: (value) => commaNumber(Number(value)),
              },
          title: {
            display: true,
            text: props.chart.series[1].measure
              ? getChartMetricLabel(props.chart.series[1].measure?.metric) ||
                'Secondary'
              : 'Secondary',
          },
        },
      },
      onHover: commonChartOptions.onHoverCursorOption,

      plugins: {
        eventPlugin: {
          events: ['mouseleave', 'click'],
        },
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          external: chartTooltip,
        },
        datalabels: {
          display: false, // Combo chart에서는 data labels 비활성화
        },
      },
    };
  }, [
    chartTooltip,
    isPrimaryAvgScoreType,
    isSecondaryAvgScoreType,
    props.chart.series,
  ]);

  return (
    <>
      <ChartJS
        ref={chartRef as any}
        type="bar"
        data={comboChartData}
        options={comboChartOptions}
        plugins={[eventPlugin, ChartDataLabels]}
      />
      {tooltipData && (
        <ChartCustomTooltip
          tooltip={tooltipData}
          meta={props.chart}
          tableData={props.tableData}
          dashboardCardInfo={props.dashboardCardInfo}
        />
      )}
    </>
  );
};

export default ComboChart;
